import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { api, type KundenInfoTeil } from '../lib/api';
import { MailModal, MAIL_SIGNATUR, type MailDraft } from '../components/MailEntwurf';

// Kundeninfo (Jasmin-Feedback): Kunden proaktiv informieren statt warten,
// bis sie selbst nachfragen. Zwei Aktionslisten:
//   1) Termin überschritten → Verzögerungs-Mail mit neuem Termin je Teil
//   2) Sammellieferung möglich → Teile desselben Kunden werden ≤5 Tage
//      auseinander fertig; eine gemeinsame Spedition spart dem Kunden Geld.
// Versand bewusst NICHT automatisch: ein Klick erzeugt einen Outlook-Entwurf
// (.eml mit Logo), ein Mensch prüft und sendet.

function fmtDatum(v?: string | null): string {
  if (!v) return '—';
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : v;
}

function teilZeile(t: KundenInfoTeil): string {
  const teile = [
    `Bestellung ${t.bestellnr}`,
    t.dv_nummer ? `DV ${t.dv_nummer}` : null,
    t.bezeichnung || null,
    `${t.stueck} Stk`,
  ].filter(Boolean);
  return teile.join(' · ');
}

type Gruppe = { kunde_id: number; kunde_name: string; kunde_email: string; teile: KundenInfoTeil[] };

function gruppiere(teile: KundenInfoTeil[]): Gruppe[] {
  const map = new Map<number, Gruppe>();
  for (const t of teile) {
    const g = map.get(t.kunde_id) ?? { kunde_id: t.kunde_id, kunde_name: t.kunde_name, kunde_email: t.kunde_email, teile: [] };
    g.teile.push(t);
    map.set(t.kunde_id, g);
  }
  return [...map.values()];
}

export default function KundenInfo() {
  const info = useApi(() => api.kundeninfo.get(), []);
  // "Neuer Termin" je Teil (Schlüssel: modul+id), fließt in den Mail-Text.
  const [neueTermine, setNeueTermine] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState<MailDraft | null>(null);

  const verspaetet = useMemo(() => gruppiere(info.data?.ueberfaellig ?? []), [info.data]);
  const sammel = info.data?.sammel ?? [];
  const sammelKundenIds = useMemo(() => new Set(sammel.map(g => g[0]?.kunde_id)), [sammel]);

  const verzoegerungsMail = (g: Gruppe) => {
    const zeilen = g.teile.map(t => {
      const neu = neueTermine[`${t.modul}:${t.id}`];
      const neuText = neu ? `neuer Termin: ${fmtDatum(neu)}` : 'neuen Termin teilen wir Ihnen kurzfristig mit';
      return `- ${teilZeile(t)} · bisher: ${fmtDatum(t.lieferdatum)} · ${neuText}`;
    }).join('\n');
    const sammelHinweis = sammelKundenIds.has(g.kunde_id)
      ? '\nHinweis: Weitere Positionen Ihrer Bestellungen werden voraussichtlich nur wenige Tage auseinander fertig. ' +
        'Gerne koordinieren wir eine gemeinsame Lieferung, damit Sie nur einmal eine Spedition beauftragen müssen.\n'
      : '';
    setDraft({
      an: g.kunde_email,
      betreff: `Aktueller Stand Ihrer Bestellung${g.teile.length > 1 ? 'en' : ''} — ${[...new Set(g.teile.map(t => t.bestellnr))].join(', ')}`,
      text:
        'Sehr geehrte Damen und Herren,\n\n' +
        'wir möchten Sie proaktiv über den aktuellen Stand folgender Positionen informieren, ' +
        'deren ursprünglicher Liefertermin sich leider verschiebt:\n\n' +
        zeilen + '\n' +
        sammelHinweis +
        '\nWir bedauern die Verzögerung und danken Ihnen für Ihr Verständnis. ' +
        'Bei Rückfragen stehen wir Ihnen selbstverständlich jederzeit zur Verfügung.\n\n' +
        MAIL_SIGNATUR,
    });
  };

  const sammelMail = (teile: KundenInfoTeil[]) => {
    const erster = teile[0];
    if (!erster) return;
    const zeilen = teile.map(t => `- ${teilZeile(t)} · voraussichtlich fertig: ${fmtDatum(t.lieferdatum)}`).join('\n');
    setDraft({
      an: erster.kunde_email,
      betreff: `Sammellieferung möglich — ${[...new Set(teile.map(t => t.bestellnr))].join(', ')}`,
      text:
        'Sehr geehrte Damen und Herren,\n\n' +
        'folgende Positionen Ihrer Bestellungen werden in kurzem Abstand zueinander fertig:\n\n' +
        zeilen + '\n\n' +
        'Wenn Sie möchten, halten wir die zuerst fertigen Teile für Sie bereit, sodass Sie alle Positionen ' +
        'mit einer einzigen Spedition abholen lassen können. Geben Sie uns dazu einfach kurz Bescheid — ' +
        'ansonsten stellen wir jede Position wie gewohnt einzeln bereit.\n\n' +
        MAIL_SIGNATUR,
    });
  };

  const ohneEmail = 'Keine E-Mail beim Kunden hinterlegt — bitte unter „Kunden" eintragen.';

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-xl font-bold text-slate-900">Kunden informieren</h1>
        <div className="text-sm text-slate-500">
          Verzögerte Termine proaktiv melden & Lieferungen bündeln — ein Klick erzeugt einen Outlook-Entwurf,
          gesendet wird von Hand. E-Mail-Adressen pflegen: <Link to="/kunden" className="text-brand hover:underline">Kunden-Stammdaten</Link>
        </div>
      </div>

      {info.loading && <div className="text-slate-500 py-8 text-center">Lädt …</div>}
      {info.error && <div className="text-red-600 py-4">Fehler beim Laden: {String(info.error.message)}</div>}

      {/* 1) Termin überschritten */}
      <div className="bg-white rounded-xl border border-red-200 overflow-hidden mb-6">
        <div className="px-4 py-3 bg-red-50 border-b border-red-200 flex items-center justify-between">
          <h2 className="font-semibold text-red-800">Termin überschritten — Kunde informieren</h2>
          <span className="text-xs text-red-700">{verspaetet.reduce((a, g) => a + g.teile.length, 0)} Teile · {verspaetet.length} Kunden</span>
        </div>
        {!info.loading && verspaetet.length === 0 && (
          <div className="px-4 py-6 text-sm text-slate-500 text-center">Kein offenes Teil ist überfällig. 🎉</div>
        )}
        {verspaetet.map(g => (
          <div key={g.kunde_id} className="border-b border-slate-100 last:border-b-0">
            <div className="px-4 py-2.5 flex flex-wrap items-center gap-2 bg-slate-50">
              <span className="font-semibold text-slate-900">{g.kunde_name}</span>
              {g.kunde_email
                ? <span className="text-xs text-slate-500">{g.kunde_email}</span>
                : <Link to="/kunden" className="text-xs text-amber-600 hover:underline">✎ keine E-Mail hinterlegt</Link>}
              {sammelKundenIds.has(g.kunde_id) && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200">📦 Sammellieferung möglich</span>
              )}
              <button onClick={() => verzoegerungsMail(g)}
                      className="ml-auto bg-brand text-white rounded-lg px-3 py-1.5 text-xs font-medium hover:opacity-90">
                ✉ Entwurf erstellen
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px] min-w-[680px]">
                <tbody className="divide-y divide-slate-100">
                  {g.teile.map(t => {
                    const key = `${t.modul}:${t.id}`;
                    return (
                      <tr key={key}>
                        <td className="px-4 py-1.5 text-xs text-slate-400 whitespace-nowrap">{t.modul}</td>
                        <td className="px-2 py-1.5 font-medium whitespace-nowrap">{t.bestellnr}</td>
                        <td className="px-2 py-1.5 text-slate-600">{t.bezeichnung ?? '—'}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{t.stueck} Stk</td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-red-600">
                          {fmtDatum(t.lieferdatum)} <span className="text-xs">({t.tage}T überfällig)</span>
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          <label className="text-xs text-slate-400 mr-1">neu:</label>
                          <input type="date" className="input !py-0.5 !px-1.5 text-xs"
                                 value={neueTermine[key] ?? ''}
                                 onChange={e => setNeueTermine(v => ({ ...v, [key]: e.target.value }))} />
                        </td>
                        <td className="px-2 py-1.5 text-xs text-slate-400 whitespace-nowrap">{t.sb ?? ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {/* 2) Sammellieferung */}
      <div className="bg-white rounded-xl border border-sky-200 overflow-hidden">
        <div className="px-4 py-3 bg-sky-50 border-b border-sky-200 flex items-center justify-between">
          <h2 className="font-semibold text-sky-800">📦 Sammellieferung möglich</h2>
          <span className="text-xs text-sky-700">offene Teile desselben Kunden, Termine ≤5 Tage auseinander</span>
        </div>
        {!info.loading && sammel.length === 0 && (
          <div className="px-4 py-6 text-sm text-slate-500 text-center">Aktuell keine Bündel-Kandidaten.</div>
        )}
        {sammel.map(teile => {
          const erster = teile[0];
          if (!erster) return null;
          return (
            <div key={erster.kunde_id} className="border-b border-slate-100 last:border-b-0">
              <div className="px-4 py-2.5 flex flex-wrap items-center gap-2 bg-slate-50">
                <span className="font-semibold text-slate-900">{erster.kunde_name}</span>
                {erster.kunde_email
                  ? <span className="text-xs text-slate-500">{erster.kunde_email}</span>
                  : <Link to="/kunden" className="text-xs text-amber-600 hover:underline">✎ keine E-Mail hinterlegt</Link>}
                <button onClick={() => sammelMail(teile)}
                        className="ml-auto bg-sky-600 text-white rounded-lg px-3 py-1.5 text-xs font-medium hover:opacity-90">
                  ✉ Bündelung vorschlagen
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px] min-w-[560px]">
                  <tbody className="divide-y divide-slate-100">
                    {teile.map(t => (
                      <tr key={`${t.modul}:${t.id}`}>
                        <td className="px-4 py-1.5 text-xs text-slate-400 whitespace-nowrap">{t.modul}</td>
                        <td className="px-2 py-1.5 font-medium whitespace-nowrap">{t.bestellnr}</td>
                        <td className="px-2 py-1.5 text-slate-600">{t.bezeichnung ?? '—'}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">{t.stueck} Stk</td>
                        <td className={`px-2 py-1.5 whitespace-nowrap ${t.tage > 0 ? 'text-red-600' : 'text-slate-700'}`}>
                          {fmtDatum(t.lieferdatum)}{t.tage > 0 && <span className="text-xs"> ({t.tage}T überfällig)</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      {draft && (
        <MailModal draft={draft} onChange={setDraft} onClose={() => setDraft(null)}
                   titel="✉ Mail an Kunde" hinweisOhneEmail={ohneEmail} />
      )}
    </div>
  );
}
